import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription, interval, filter, skip } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';

interface DebugLog {
  timestamp: Date;
  type: 'info' | 'warning' | 'error' | 'success';
  message: string;
}

interface CookieInfo {
  name: string;
  value: string;
  hasSecure: boolean;
  hasSameSite: boolean;
  sameSiteValue: string;
}

@Component({
  selector: 'app-iframe',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="iframe-container">
      <!-- ToDo: 2026-03-03 BK IMPORTANT Activate before RollOut -->
        @if (isDebugUser()) {

        <!-- Debug Panel Toggle -->
        <button class="debug-toggle" (click)="toggleDebug()" title="Cookie Debug">
          🔍 {{ showDebug ? 'Debug verbergen' : 'Debug anzeigen' }}
        </button>
      }

      <!-- Debug Panel -->
      <div class="debug-panel" *ngIf="showDebug">
        <div class="debug-header">
          <h3>🔍 SSO & Cookie Debug</h3>
          <button (click)="clearDebugLogs()">Clear</button>
        </div>

        <!-- Current URL Section -->
        <div class="debug-section">
          <h4>🌐 Current URL</h4>
          <div class="url-display">
            <div class="url-box">
              <strong>Target:</strong>
              <div class="url-value">{{ currentUrl || 'Keine URL geladen' }}</div>
            </div>
            <div class="url-box" *ngIf="title">
              <strong>Title:</strong>
              <div class="url-value">{{ title }}</div>
            </div>
          </div>
        </div>

        <!-- Cookie Status -->
        <div class="debug-section">
          <h4>🍪 Cookie Status</h4>
          <div class="cookie-list">
            <div *ngFor="let cookie of cookies"
                 class="cookie-item"
                 [class.cookie-valid]="cookie.hasSecure && cookie.hasSameSite"
                 [class.cookie-invalid]="!cookie.hasSecure || !cookie.hasSameSite">
              <strong>{{ cookie.name }}</strong>
              <div class="cookie-details">
                <span [class.tag-success]="cookie.hasSecure" [class.tag-error]="!cookie.hasSecure">
                  {{ cookie.hasSecure ? '✓' : '✗' }} Secure
                </span>
                <span [class.tag-success]="cookie.hasSameSite && cookie.sameSiteValue === 'None'"
                      [class.tag-error]="!cookie.hasSameSite || cookie.sameSiteValue !== 'None'">
                  {{ cookie.hasSameSite ? '✓' : '✗' }} SameSite={{ cookie.sameSiteValue || 'missing' }}
                </span>
              </div>
            </div>
            <div *ngIf="cookies.length === 0" class="no-cookies">
              ⚠️ Keine Cookies gefunden
            </div>
          </div>
        </div>

        <!-- Flow Status -->
        <div class="debug-section">
          <h4>📊 SSO Flow Status</h4>
          <div class="flow-status">
            <div class="status-item" [class.active]="flowStage >= 1">
              <span class="status-icon">{{ flowStage >= 1 ? '✓' : '○' }}</span>
              iFrame geladen
            </div>
            <div class="status-item" [class.active]="flowStage >= 2">
              <span class="status-icon">{{ flowStage >= 2 ? '✓' : '○' }}</span>
              Keycloak Redirect
            </div>
            <div class="status-item" [class.active]="flowStage >= 3">
              <span class="status-icon">{{ flowStage >= 3 ? '✓' : '○' }}</span>
              OAuth Code erhalten
            </div>
            <div class="status-item" [class.active]="flowStage >= 4">
              <span class="status-icon">{{ flowStage >= 4 ? '✓' : '○' }}</span>
              Session Cookie gesetzt
            </div>
          </div>
        </div>

        <!-- Debug Logs -->
        <div class="debug-section">
          <h4>📝 Debug Logs</h4>
          <div class="debug-logs">
            <div *ngFor="let log of debugLogs"
                 class="log-entry"
                 [class.log-info]="log.type === 'info'"
                 [class.log-warning]="log.type === 'warning'"
                 [class.log-error]="log.type === 'error'"
                 [class.log-success]="log.type === 'success'">
              <span class="log-time">{{ log.timestamp | date:'HH:mm:ss.SSS' }}</span>
              <span class="log-message">{{ log.message }}</span>
            </div>
          </div>
        </div>

        <!-- Test Actions -->
        <div class="debug-section">
          <h4>🧪 Test Actions</h4>
          <div class="test-actions">
            <button class="btn-test" (click)="testCookies()">
              Test Cookies
            </button>
            <button class="btn-test" (click)="openInNewTab()">
              Open in New Tab
            </button>
            <button class="btn-test" (click)="checkThirdPartyCookies()">
              Check 3rd Party Cookies
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div class="iframe-loading" *ngIf="isLoading">
        <div class="spinner"></div>
        <p>Lade Inhalt...</p>
        <small>{{ loadingMessage }}</small>
      </div>

      <!-- Main iframe -->
      <iframe
        #iframeElement
        *ngIf="safeUrl && !hasError"
        [src]="safeUrl"
        [style.display]="isLoading ? 'none' : 'block'"
        (load)="onIframeLoad()"
        (error)="onIframeError()"
        frameborder="0"
        width="100%"
        height="100%"
        style="min-height: 400px;"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads">
        Ihr Browser unterstützt keine iframes.
      </iframe>

      <!-- Error State -->
      <div class="iframe-error" *ngIf="hasError">
        <div class="error-content">
          <i class="fa" *ngIf="errorType !== 'auth-redirect'"
             [class.fa-ban]="errorType === 'blocked'"
             [class.fa-exclamation-triangle]="errorType !== 'blocked'"></i>

          <ng-container *ngIf="errorType === 'blocked'">
            <h4>Verbindung abgelehnt</h4>
            <p>Die externe Anwendung hat die Einbettung in einem iFrame abgelehnt.</p>
            <p class="error-detail">
              Dies ist eine Sicherheitsmaßnahme der Zielanwendung (X-Frame-Options / CSP).
            </p>
            <p><strong>URL:</strong> {{currentUrl}}</p>
            <button class="btn-new-tab" (click)="openInNewTab()">
              <i class="fa fa-external-link"></i> In neuem Tab öffnen
            </button>
          </ng-container>

          <ng-container *ngIf="errorType === 'recursive'">
            <h4>Ungültige Konfiguration</h4>
            <p>Die Anwendung kann nicht sich selbst in einem iFrame laden.</p>
            <p class="error-detail">
              Bitte überprüfen Sie die Konfiguration des Menüpunkts.
            </p>
            <p><strong>URL:</strong> {{currentUrl}}</p>
            <button class="btn-retry" (click)="goBack()">Zurück</button>
          </ng-container>

          <ng-container *ngIf="errorType === 'auth-redirect'">
            <h4>Anmeldung nicht möglich</h4>
            <p>Ihre Anmeldung konnte nicht abgeschlossen werden. Bitte wenden Sie sich an Ihre zuständige Kundenbetreuerin bzw. Ihren zuständigen Kundenbetreuer.</p>
          </ng-container>

          <ng-container *ngIf="errorType === 'generic'">
            <h4>Fehler beim Laden</h4>
            <p>Die Seite konnte nicht geladen werden.</p>
            <p><strong>URL:</strong> {{currentUrl}}</p>
            <button class="btn-retry" (click)="retryLoad()">Erneut versuchen</button>
            <button class="btn-new-tab" (click)="openInNewTab()">In neuem Tab öffnen</button>
          </ng-container>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: calc(100vh - 100px);
      flex-shrink: 0;
      overflow: hidden;
    }

    .iframe-container {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    /* Debug Toggle Button */
    .debug-toggle {
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 1000;
      background: #2c3e50;
      color: white;
      border: none;
      padding: 8px 15px;
      border-radius: 5px;
      cursor: pointer;
      font-size: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    }

    .debug-toggle:hover {
      background: #34495e;
    }

    /* Debug Panel */
    .debug-panel {
      position: absolute;
      top: 50px;
      right: 10px;
      width: 400px;
      max-height: calc(100% - 60px);
      background: white;
      border: 1px solid #ddd;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      z-index: 999;
      overflow-y: auto;
      font-size: 13px;
    }

    .debug-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 15px;
      background: #2c3e50;
      color: white;
      border-radius: 8px 8px 0 0;
    }

    .debug-header h3 {
      margin: 0;
      font-size: 16px;
    }

    .debug-header button {
      background: #e74c3c;
      color: white;
      border: none;
      padding: 5px 10px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    }

    .debug-section {
      padding: 15px;
      border-bottom: 1px solid #eee;
    }

    .debug-section h4 {
      margin: 0 0 10px 0;
      font-size: 14px;
      color: #2c3e50;
    }

    /* Cookie List */
    .cookie-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .cookie-item {
      padding: 10px;
      background: #f8f9fa;
      border-radius: 4px;
      border-left: 3px solid #95a5a6;
    }

    .cookie-item.cookie-valid {
      border-left-color: #27ae60;
    }

    .cookie-item.cookie-invalid {
      border-left-color: #e74c3c;
    }

    .cookie-item strong {
      display: block;
      margin-bottom: 5px;
      color: #2c3e50;
    }

    .cookie-details {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .cookie-details span {
      padding: 2px 8px;
      border-radius: 3px;
      font-size: 11px;
      font-weight: 500;
    }

    .tag-success {
      background: #d4edda;
      color: #155724;
    }

    .tag-error {
      background: #f8d7da;
      color: #721c24;
    }

    .no-cookies {
      padding: 10px;
      text-align: center;
      color: #95a5a6;
      font-style: italic;
    }

    /* Flow Status */
    .flow-status {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .status-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
      color: #95a5a6;
    }

    .status-item.active {
      background: #d4edda;
      color: #155724;
    }

    .status-icon {
      font-weight: bold;
      font-size: 16px;
    }

    /* Debug Logs */
    .debug-logs {
      max-height: 200px;
      overflow-y: auto;
      background: #f8f9fa;
      border-radius: 4px;
      padding: 8px;
    }

    .log-entry {
      display: flex;
      gap: 8px;
      padding: 4px 8px;
      margin-bottom: 4px;
      border-radius: 3px;
      font-size: 11px;
      font-family: 'Courier New', monospace;
    }

    .log-info {
      background: #d1ecf1;
      color: #0c5460;
    }

    .log-warning {
      background: #fff3cd;
      color: #856404;
    }

    .log-error {
      background: #f8d7da;
      color: #721c24;
    }

    .log-success {
      background: #d4edda;
      color: #155724;
    }

    .log-time {
      font-weight: bold;
      white-space: nowrap;
    }

    .log-message {
      flex: 1;
    }

    /* Test Actions */
    .test-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .btn-test {
      padding: 8px 12px;
      background: #3498db;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      transition: background 0.2s;
    }

    .btn-test:hover {
      background: #2980b9;
    }

    /* URL Display */
    .url-display {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .url-box {
      background: #f8f9fa;
      padding: 10px;
      border-radius: 4px;
      border-left: 3px solid #3498db;
    }

    .url-box strong {
      display: block;
      margin-bottom: 5px;
      color: #2c3e50;
      font-size: 12px;
    }

    .url-value {
      color: #3498db;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      word-break: break-all;
      line-height: 1.4;
    }

    /* Original Styles */
    iframe {
      flex: 1;
      border: none;
      width: 100%;
      height: 100%;
      display: block;
    }

    .iframe-loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      z-index: 10;
      background: white;
      padding: 30px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .iframe-loading small {
      display: block;
      margin-top: 10px;
      color: #666;
      font-size: 12px;
    }

    .spinner {
      border: 4px solid #f3f3f3;
      border-top: 4px solid #CC0F3C;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      animation: spin 1s linear infinite;
      margin: 0 auto 15px;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .iframe-error {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      z-index: 10;
    }

    .error-content {
      background: white;
      padding: 30px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      max-width: 500px;
    }

    .error-content i {
      font-size: 3rem;
      margin-bottom: 15px;
    }

    .error-content i.fa-ban {
      color: #f39c12;
    }

    .error-content i.fa-exclamation-triangle {
      color: #dc3545;
    }

    .error-content h4 {
      color: #333;
      margin-bottom: 15px;
      font-size: 1.5rem;
    }

    .error-content p {
      color: #666;
      margin-bottom: 10px;
      line-height: 1.5;
    }

    .error-detail {
      font-size: 0.9rem;
      color: #888;
      font-style: italic;
    }

    .btn-retry, .btn-new-tab {
      background-color: #CC0F3C;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.2s;
      margin: 5px;
      font-size: 14px;
    }

    .btn-retry:hover {
      background-color: #a00c30;
    }

    .btn-new-tab {
      background-color: #28a745;
    }

    .btn-new-tab:hover {
      background-color: #1e7e34;
    }

    .btn-new-tab i {
      margin-right: 5px;
    }

    /* Responsive */
    @media (max-width: 768px) {
      .debug-panel {
        width: calc(100% - 20px);
        right: 10px;
      }

      .error-content {
        margin: 20px;
        padding: 20px;
      }
    }
  `]
})
export class IframeComponent implements OnInit, OnDestroy {
  @ViewChild('iframeElement', { read: ElementRef }) iframeElement?: ElementRef<HTMLIFrameElement>;

  safeUrl: SafeResourceUrl | null = null;
  currentUrl = '';
  title = '';
  isLoading = true;
  hasError = false;
  errorType: 'generic' | 'blocked' | 'recursive' | 'auth-redirect' = 'generic';
  loadingMessage = 'Initialisierung...';

  // Debug
  showDebug = false;
  debugLogs: DebugLog[] = [];
  cookies: CookieInfo[] = [];
  flowStage = 0;

  private routeSubscription?: Subscription;
  private loadingTimeout?: number;
  private cookieCheckInterval?: Subscription;
  private iframeCheckInterval?: number;
  private currentHostname = window.location.hostname;
  private loadCount = 0;

  // ToDo: EMS-1133 entfernen sobald QM-App redirect_uri korrekt konfiguriert ist
  private readonly authRedirectBypassUrls: string[] = [
    'https://ems.smart-media.world/qm/'
  ];

  private readonly debugUsers: string[] = [
    'k.breit@sommer-gossmann.com',
    'b.krech@sommer-gossmann.com',
    'patrick@gendrisch.com',
    'jespersen@unitedpartners.de'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.addDebugLog('info', 'Component initialized');
    this.addDebugLog('info', `Current hostname: ${this.currentHostname}`);
    this.startCookieMonitoring();

    const routeData = this.route.snapshot.data;
    const routeParams = this.route.snapshot.params;

    if (routeData['url']) {
      this.currentUrl = this.getExternUrl(this.authService.getCurrentClient(), routeData['url']);
      this.title = routeData['title'] || 'Externe Anwendung';
      if(routeData['target'] === 'tab') {
        this.openInNewTab();
        this.isLoading = false;
        this.loadingMessage = 'Inhalte in neuem Tab geöffnet';
        this.cdr.detectChanges();

        // Bei erneutem Klick auf denselben Menüpunkt wird ngOnInit nicht nochmals aufgerufen
        // (RouteReuseStrategy gibt true zurück für gleiche routeConfig).
        // Deshalb auf NavigationEnd subscriben und openInNewTab() erneut auslösen.
        // skip(1) überspringt das NavigationEnd der aktuellen (initialen) Navigation.
        this.routeSubscription = this.router.events.pipe(
          filter(e => e instanceof NavigationEnd),
          skip(1)
        ).subscribe(() => {
          if (this.currentUrl) {
            this.openInNewTab();
          }
        });
      } else {
        this.loadUrl(this.currentUrl);
      }
      return;
    }

    if (routeParams['service']) {
      this.handleDynamicService(routeParams['service']);
      return;
    }

    this.routeSubscription = this.route.queryParams.subscribe(params => {
      const url = params['url'];
      const title = params['title'] || 'Externe Anwendung';

      if (url) {
        this.currentUrl = url;
        this.title = title;
        this.loadUrl(url);
      } else {
        this.handleError('Keine URL angegeben', 'generic');
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
    if (this.cookieCheckInterval) {
      this.cookieCheckInterval.unsubscribe();
    }
    if (this.iframeCheckInterval) {
      clearInterval(this.iframeCheckInterval);
    }
  }

  isDebugUser(): boolean {
    const email = this.authService.getCurrentUserEmail();
    return this.debugUsers.some(u => u.toLowerCase() === email.toLowerCase());
  }

  // Debug Functions
  toggleDebug(): void {
    this.showDebug = !this.showDebug;
    if (this.showDebug) {
      this.updateCookies();
      this.addDebugLog('info', 'Debug panel opened');
    }
  }

  addDebugLog(type: 'info' | 'warning' | 'error' | 'success', message: string): void {
    this.debugLogs.unshift({
      timestamp: new Date(),
      type,
      message
    });

    // Keep only last 50 logs
    if (this.debugLogs.length > 50) {
      this.debugLogs = this.debugLogs.slice(0, 50);
    }

    console.log(`[${type.toUpperCase()}] ${message}`);
  }

  clearDebugLogs(): void {
    this.debugLogs = [];
    this.addDebugLog('info', 'Logs cleared');
  }

  startCookieMonitoring(): void {
    // Check cookies every 2 seconds
    this.cookieCheckInterval = interval(2000).subscribe(() => {
      this.updateCookies();
    });
  }

  updateCookies(): void {
    const cookieString = document.cookie;
    const cookieArray = cookieString.split(';').map(c => c.trim()).filter(c => c);

    this.cookies = cookieArray.map(cookie => {
      const [name, value] = cookie.split('=');
      return {
        name: name.trim(),
        value: value?.trim() || '',
        hasSecure: this.checkCookieAttribute(name, 'Secure'),
        hasSameSite: this.checkCookieAttribute(name, 'SameSite'),
        sameSiteValue: this.getCookieSameSiteValue(name)
      };
    });

    // Check for PHPSESSID
    const hasPhpSession = this.cookies.some(c => c.name === 'PHPSESSID');
    if (hasPhpSession && this.flowStage < 4) {
      this.flowStage = 4;
      this.addDebugLog('success', 'PHPSESSID cookie detected!');
    }

    this.cdr.detectChanges();
  }

  checkCookieAttribute(cookieName: string, attribute: string): boolean {
    // This is a simplified check - in reality, we can't read HttpOnly/Secure/SameSite from document.cookie
    // But we log what we can see
    return document.cookie.includes(cookieName);
  }

  getCookieSameSiteValue(cookieName: string): string {
    // Can't actually read this from document.cookie
    // This is a placeholder - real detection would need server-side help
    return 'unknown';
  }

  testCookies(): void {
    this.addDebugLog('info', 'Testing cookie access...');
    this.updateCookies();

    const hasCookies = this.cookies.length > 0;
    if (hasCookies) {
      this.addDebugLog('success', `Found ${this.cookies.length} cookies`);
    } else {
      this.addDebugLog('warning', 'No cookies found - may be blocked by browser');
    }
  }

  checkThirdPartyCookies(): void {
    this.addDebugLog('info', 'Checking third-party cookie support...');

    // Create a test cookie
    document.cookie = 'test_cookie=1; SameSite=None; Secure';

    setTimeout(() => {
      const testExists = document.cookie.includes('test_cookie');
      if (testExists) {
        this.addDebugLog('success', 'Third-party cookies seem to be enabled');
        document.cookie = 'test_cookie=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      } else {
        this.addDebugLog('error', 'Third-party cookies may be blocked!');
      }
    }, 100);
  }

  private isAuthRedirectBypassed(): boolean {
    return this.authRedirectBypassUrls.some(url => this.currentUrl.startsWith(url));
  }

  // Prüft ob die URL eine in den Environment-URLs konfigurierte externe Anwendung ist.
  // Externe Apps können auf dem gleichen Hostname laufen (z.B. /qm/ auf stage-ems.smart-media.world)
  // und dürfen nicht als rekursiv blockiert werden.
  private isConfiguredExternalUrl(url: string): boolean {
    const extUrls = environment.externApplicationUrls;
    if (!extUrls) return false;
    const allUrls: string[] = [
      ...(extUrls['sug']?.map((a: any) => a.url) ?? []),
      ...(extUrls['ems']?.map((a: any) => a.url) ?? [])
    ].filter(Boolean);
    return allUrls.some((extUrl: string) => url.startsWith(extUrl));
  }

  // Check if URL is trying to load own application
  private isRecursiveUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname !== this.currentHostname) return false;

      // Gleicher Hostname: externe Apps auf demselben Server sind keine Rekursion
      if (this.isConfiguredExternalUrl(url)) return false;

      // Keycloak-Login- und Auth-Endpoints auf demselben Hostname sind kein rekursiver Load
      // (passiert beim SSO-Flow wenn externe App auf gleicher Domain wie Stammdaten-App läuft)
      const keycloakUrls = [environment.LOGIN_URL_BASE, environment.LOGIN_URL_SUG].filter(Boolean);
      if (keycloakUrls.some(loginUrl => url.startsWith(loginUrl))) return false;
      if (urlObj.pathname.startsWith('/auth/') || urlObj.pathname.startsWith('/realms/')) return false;

      this.addDebugLog('warning', `Recursive URL detected: ${url}`);
      return true;
    } catch {
      return false;
    }
  }

  // Überwacht den iframe auf rekursives Laden (eigene App wird im iframe geöffnet).
  // Funktioniert für Same-Origin UND Cross-Origin URLs:
  // - Bei Cross-Origin: contentWindow.location wirft SecurityError → normal, weiter prüfen
  // - Wenn Cross-Origin-iframe plötzlich Same-Origin wird → Redirect auf eigene Domain erkannt!
  private startIframeMonitoring(): void {
    if (this.iframeCheckInterval) {
      clearInterval(this.iframeCheckInterval);
    }

    const isCrossOrigin = this.isCrossOrigin(this.currentUrl);
    let checkCount = 0;
    const maxChecks = 15; // 15 Sekunden überwachen

    this.addDebugLog('info', `Starting iframe monitoring (cross-origin: ${isCrossOrigin})`);

    this.iframeCheckInterval = window.setInterval(() => {
      checkCount++;

      try {
        if (!this.iframeElement?.nativeElement) return;
        const iframe = this.iframeElement.nativeElement;

        try {
          // Wenn dieser Zugriff NICHT wirft, ist der iframe Same-Origin.
          const iframeUrl = iframe.contentWindow?.location.href || '';

          if (iframeUrl && this.isRecursiveUrl(iframeUrl)) {
            if (this.iframeCheckInterval) {
              clearInterval(this.iframeCheckInterval);
            }

            // Cross-Origin-URL die plötzlich Same-Origin ist = Auth-Redirect
            const errorType = isCrossOrigin ? 'auth-redirect' : 'recursive';
            this.addDebugLog('error', `Redirect auf eigene Domain erkannt: ${iframeUrl}`);

            if (errorType === 'auth-redirect' && this.isAuthRedirectBypassed()) {
              this.addDebugLog('warning', `auth-redirect bypassed für: ${this.currentUrl}`);
              return;
            }

            this.handleError(
              isCrossOrigin
                ? 'Authentifizierung fehlgeschlagen - Redirect auf Hauptseite'
                : 'Rekursives Laden erkannt',
              errorType
            );
            return;
          }

          // Same-Origin, aber nicht rekursiv → leerer Body prüfen
          if (!isCrossOrigin) {
            const body = iframe.contentDocument?.body;
            if (body && body.innerHTML.trim() === '') {
              this.addDebugLog('warning', `iFrame body is empty (check ${checkCount}/${maxChecks})`);
            }
          }
        } catch (e) {
          // SecurityError = iframe ist noch Cross-Origin → normal, weiter prüfen
        }
      } catch (error) {
        this.addDebugLog('warning', `iFrame check error: ${error}`);
      }

      if (checkCount >= maxChecks) {
        if (this.iframeCheckInterval) {
          clearInterval(this.iframeCheckInterval);
        }
        this.addDebugLog('info', 'iframe monitoring stopped (max checks reached)');
      }
    }, 1000);
  }

  // Original Functions
  private loadUrl(url: string): void {
    if (!this.isValidUrl(url)) {
      this.handleError('Ungültige URL', 'generic');
      return;
    }

    // Check for recursive loading
    if (this.isRecursiveUrl(url)) {
      this.handleError('Rekursives Laden - URL zeigt auf diese Anwendung', 'recursive');
      return;
    }

    try {
      this.addDebugLog('info', `Loading URL: ${url}`);
      this.isLoading = true;
      this.hasError = false;
      this.safeUrl = null;
      this.flowStage = 1;
      this.loadCount = 0;
      this.loadingMessage = 'Verbinde mit Server...';
      this.cdr.detectChanges();

      setTimeout(() => {
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.loadingMessage = 'Lade Inhalte...';
        this.cdr.detectChanges();
      }, 50);

      this.loadingTimeout = window.setTimeout(() => {
        if (this.isLoading) {
          this.addDebugLog('error', 'Loading timeout - possible X-Frame-Options block');
          this.handleError('Timeout - externe Anwendung hat möglicherweise die Verbindung abgelehnt', 'blocked');
        }
      }, 15000);

    } catch (error) {
      console.error('Error loading URL:', error);
      this.handleError('Fehler beim Laden der URL', 'generic');
    }

    // Monitoring nur für Same-Origin URLs starten (startIframeMonitoring prüft das auch nochmal)
    setTimeout(() => {
      this.startIframeMonitoring();
    }, 500);
  }

  private isCrossOrigin(url: string): boolean {
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname !== this.currentHostname) return true;
      // Gleicher Hostname, aber konfigurierte externe App → als cross-origin behandeln,
      // damit Auth-Redirects korrekt als 'auth-redirect' und nicht als 'recursive' erkannt werden.
      return this.isConfiguredExternalUrl(url);
    } catch {
      return true;
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private handleError(message: string, type: 'generic' | 'blocked' | 'recursive' | 'auth-redirect' = 'generic'): void {
    this.addDebugLog('error', message);
    this.errorType = type;

    setTimeout(() => {
      this.hasError = true;
      this.isLoading = false;
      this.safeUrl = null;
      this.cdr.detectChanges();
    }, 0);

    // Stop monitoring
    if (this.iframeCheckInterval) {
      clearInterval(this.iframeCheckInterval);
    }
  }

  onIframeLoad(): void {
    this.loadCount++;
    this.addDebugLog('success', `iFrame loaded (load #${this.loadCount})`);
    this.flowStage = Math.max(this.flowStage, 2);

    // Bei jedem Load-Event prüfen ob der iframe auf die eigene Domain umgeleitet wurde.
    setTimeout(() => {
      try {
        if (this.iframeElement?.nativeElement.contentWindow) {
          const iframeUrl = this.iframeElement.nativeElement.contentWindow.location.href;

          if (this.isRecursiveUrl(iframeUrl)) {
            if (this.isCrossOrigin(this.currentUrl)) {
              if (this.isAuthRedirectBypassed()) {
                this.addDebugLog('warning', `auth-redirect bypassed für: ${this.currentUrl}`);
              } else {
                this.addDebugLog('error', `Auth redirect detected: external app redirected back to ${iframeUrl}`);
                this.handleError('Authentifizierung fehlgeschlagen - Redirect auf Hauptseite', 'auth-redirect');
                return;
              }
            } else {
              this.addDebugLog('error', 'Recursive content detected after load');
              this.handleError('Rekursives Laden erkannt', 'recursive');
              return;
            }
          }
        }
      } catch (e) {
        // Cross-origin Zugriff blockiert – das ist normal und erwartet
        this.addDebugLog('info', 'Cross-origin iframe URL check blocked (expected)');
      }

      this.isLoading = false;
      this.hasError = false;
      this.loadingMessage = '';
      this.cdr.detectChanges();
    }, 100);

    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    // OAuth-Code-Check nur für Same-Origin URLs
    if (!this.isCrossOrigin(this.currentUrl)) {
      this.checkForOAuthCode();
    }
  }


  checkForOAuthCode(): void {
    try {
      const iframe = document.querySelector('iframe');
      if (iframe && iframe.contentWindow) {
        const iframeUrl = iframe.contentWindow.location.href;
        if (iframeUrl.includes('code=')) {
          this.flowStage = 3;
          this.addDebugLog('success', 'OAuth code detected in URL');
        }
      }
    } catch (e) {
      // Cross-origin access blocked - this is expected
      this.addDebugLog('warning', 'Cannot access iframe URL (cross-origin)');
    }
  }

  onIframeError(): void {
    this.addDebugLog('error', 'iFrame failed to load - possibly blocked by X-Frame-Options');
    this.handleError('iFrame konnte nicht geladen werden - externe Anwendung hat Verbindung möglicherweise abgelehnt', 'blocked');
  }

  retryLoad(): void {
    if (this.currentUrl) {
      this.addDebugLog('info', 'Retrying load...');
      setTimeout(() => {
        this.flowStage = 0;
        this.errorType = 'generic';
        this.loadUrl(this.currentUrl);
      }, 0);
    }
  }

  goBack(): void {
    this.addDebugLog('info', 'Going back...');
    window.history.back();
  }

  openInNewTab(): void {
    if (this.currentUrl) {
      this.addDebugLog('info', 'Opening in new tab');
      window.open(this.currentUrl, '_blank');
    }
  }

  getExternUrl(domain: string, path: string): string {
    if (domain == 'sug') {
      return environment.externApplicationUrls?.sug.find(app => app.path === path)?.url ?? '';
    }
    if (domain == 'ems') {
      return environment.externApplicationUrls?.ems.find(app => app.path === path)?.url ?? '';
    }
    return '';
  }

  private handleDynamicService(service: string): void {
    this.addDebugLog('info', `Handling dynamic service: ${service}`);

    const serviceUrls: { [key: string]: { url: string, title: string } } = {
      'reklamation': {
        url: 'https://reklamation.sommer-gossmann.com',
        title: 'Reklamation'
      },
      'digitalperformance': {
        url: 'https://digitalperformance.sommer-gossmann.com',
        title: 'Digital Performance'
      },
      'mediamaps': {
        url: 'https://mediamaps.sommer-gossmann.com',
        title: 'Media Maps'
      }
    };

    const serviceConfig = serviceUrls[service];
    if (serviceConfig) {
      this.currentUrl = serviceConfig.url;
      this.title = serviceConfig.title;
      this.loadUrl(this.currentUrl);
    } else {
      this.handleError('Service nicht gefunden', 'generic');
    }
  }

  protected readonly environment = environment;
}
